import React, { useEffect } from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';

import FallbackPage from './FallbackPage';
import {
  ASSET_NAME,
  featuredSectionId,
  fetchFeaturedListings,
  fetchThemedCollections,
  fetchRuleBasedCollections,
} from './LandingPage.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { useConfiguration } from '../../context/configurationContext';
import SectionFeaturedListings from '../PageBuilder/SectionBuilder/SectionFeaturedListings/SectionFeaturedListings';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

// Fisher-Yates shuffle algorithm - efficient and unbiased array shuffling
const shuffleArray = array => {
  const shuffled = [...array]; // Create a copy to avoid mutating the original
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const isSoldOut = listing => {
  const stock = listing?.currentStock?.attributes?.quantity;
  return typeof stock === 'number' && stock === 0;
};

export const LandingPageComponent = props => {
  const config = useConfiguration();
  const {
    pageAssetsData,
    inProgress,
    error,
    featuredListings,
    themedCollections,
    ruleBasedCollections,
    onFetchFeaturedListings,
    onFetchThemedCollections,
    onFetchRuleBasedCollections,
  } = props;
  const { featuredSectionType } = config.listing;

  useEffect(() => {
    onFetchFeaturedListings(config);
  }, [config, onFetchFeaturedListings]);

  useEffect(() => {
    // Fetch themed collections after page assets are loaded
    if (pageAssetsData && !inProgress) {
      onFetchThemedCollections(config, pageAssetsData);
    }
  }, [config, pageAssetsData, inProgress, onFetchThemedCollections]);

  useEffect(() => {
    // Rule-based collections are query-driven and don't depend on page assets,
    // so they can fetch as soon as config is available (in parallel with the
    // fetches above - it doesn't block the initial render).
    onFetchRuleBasedCollections(config);
  }, [config, onFetchRuleBasedCollections]);

  // Construct custom page data
  const pageData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;

  // Find the correct custom section based on section id
  const featuredSectionIdx = pageData?.sections.findIndex(s => s.sectionId === featuredSectionId);
  const featuredSection = pageData?.sections[featuredSectionIdx];

  // Define the necessary props for the custom section
  const customFeaturedSection = {
    ...featuredSection,
    sectionId: featuredSectionId,
    sectionType: featuredSectionType,
    listings: featuredListings,
  };

  // Replace the original section with the custom section object
  // in custom page data
  let customSections =
    pageData?.sections?.map((s, idx) => (idx === featuredSectionIdx ? customFeaturedSection : s)) ||
    null;

  // Manually curated (themed) and rule-based collections render identically and
  // follow the same rules, so combine them into a single list of carousels.
  const collections = React.useMemo(
    () => [...(themedCollections || []), ...(ruleBasedCollections || [])],
    [themedCollections, ruleBasedCollections]
  );

  // Memoize the shuffled order of listing IDs for each collection
  // This ensures the order doesn't change when individual listing data updates (e.g. hearting)
  const shuffledCollections = React.useMemo(() => {
    return collections.map(collection => ({
      ...collection,
      shuffledIds: shuffleArray(collection.listingIds),
    }));
  }, [collections]);

  // Add themed collection sections after the featured section
  if (customSections && shuffledCollections.length > 0) {
    // Find the index where to insert themed collections (after featured section)
    const insertIndex = featuredSectionIdx + 1;

    // Create themed collection sections
    // Copy the structure from the featured section to maintain consistent styling
    const themedSections = shuffledCollections.map(collection => {
      // Get listings using the pre-shuffled IDs
      const listings = getListingsById({ marketplaceData: { entities: props.entities } }, collection.shuffledIds);
      const activeListings = listings.filter(l => !isSoldOut(l));

      return {
        ...featuredSection, // Copy all properties from featured section (styling, appearance, etc.)
        sectionId: collection.sectionId,
        sectionType: featuredSectionType,
        title: {
          ...featuredSection?.title, // Copy title styling from featured section
          content: collection.title, // But use our collection title
        },
        listings: activeListings,
      };
    });

    // Insert themed sections after featured section
    customSections = [
      ...customSections.slice(0, insertIndex),
      ...themedSections,
      ...customSections.slice(insertIndex),
    ];
  }

  const customPageData = pageData
    ? {
      ...pageData,
      sections: customSections,
    }
    : pageData;

  return (
    <PageBuilder
      pageAssetsData={customPageData}
      options={{
        sectionComponents: {
          [featuredSectionType]: { component: SectionFeaturedListings },
        },
      }}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
    />
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const { featuredListingIds, themedCollections, ruleBasedCollections } = state.LandingPage;
  const featuredListings = getListingsById(state, featuredListingIds).filter(l => !isSoldOut(l));
  const entities = state.marketplaceData?.entities;

  return {
    pageAssetsData,
    inProgress,
    error,
    featuredListings,
    themedCollections,
    ruleBasedCollections,
    entities,
  };
};

const mapDispatchToProps = dispatch => ({
  onFetchFeaturedListings: config => dispatch(fetchFeaturedListings(config)),
  onFetchThemedCollections: (config, pageAssetsData) =>
    dispatch(fetchThemedCollections(config, pageAssetsData)),
  onFetchRuleBasedCollections: config => dispatch(fetchRuleBasedCollections(config)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(LandingPageComponent);

export default LandingPage;
